import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, ImageOff, ImagePlus, Trash2 } from "lucide-react";
import { type ReactNode, useRef, useState } from "react";
import { useConfirm } from "../../web/components/Confirm";
import { WindowPopup } from "../../web/components/WindowPopup";
import { Box, ErrorNotice, Spinner } from "../../web/components/ui";
import { api, withSession } from "../../web/lib/api";
import { useCompany } from "../../web/lib/company";
import { plural } from "../../shared/words";
import { can } from "./data";
import { PHOTO_CAPTION_MAX, type PhotoKind, type PhotoRow, PHOTOS_BY, PHOTOS_MAX, type PriceListData } from "./meta";

/*
 * A product's or group's photos (photos.ts): to show a customer what it is, or how it's been fitted. A grid of them, in order,
 * each with its caption; a click shows it full size. Whoever may change it adds them (camera or files: several at once), captions,
 * moves and deletes them (why someone can't is said by its window). Each is shrunk here before it's sent (a phone's 5 MB photo is about 300 KB), as a JPEG, which leaves
 * out what the camera kept in it (where it was taken).
 */

/** The photo's longest side, and its copy's for the grid. */
const PHOTO_SIDE = 1600;
const SMALL_SIDE = 480;

/** The file, at most `side` pixels across or down, as a JPEG (turned as the camera held it). */
async function shrink(file: File, side: number): Promise<{ blob: Blob; width: number; height: number }> {
    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" }).catch(() => {
        throw new Error(`${file.name} can't be read as a photo here. Try a JPEG or PNG.`);
    });
    const scale = Math.min(1, side / Math.max(bitmap.width, bitmap.height));
    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("This browser can't prepare photos");
    // White behind a PNG's see-through parts (a JPEG has none: they'd be black).
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.85));
    if (!blob) throw new Error("This browser can't prepare photos");
    return { blob, width, height };
}

/** Its photos, updated live (photos.ts publishes each change under this key). Its window counts them on the tab, as the tab shows them. */
export function usePhotos(kind: PhotoKind, id: string, enabled = true) {
    const company = useCompany();
    const key = ["c", company.id, "pricelist", "photos", kind, id];
    return { key, query: useQuery({ queryKey: key, queryFn: () => api<PhotoRow[]>(`/c/${company.id}/tools/pricelist/photos/${kind}/${id}`), enabled }) };
}

export function PhotosTab({ data, kind, id }: Readonly<{ data: PriceListData; kind: PhotoKind; id: string }>) {
    const company = useCompany();
    const qc = useQueryClient();
    const confirm = useConfirm();
    const base = `/c/${company.id}/tools/pricelist`;
    const { key, query } = usePhotos(kind, id);
    const photos = query.data ?? [];
    const editable = can(data, PHOTOS_BY[kind]);
    const input = useRef<HTMLInputElement>(null);
    const [adding, setAdding] = useState<string | null>(null);
    const [error, setError] = useState<Error | null>(null);
    const [open, setOpen] = useState<PhotoRow | null>(null);
    const refresh = () => qc.invalidateQueries({ queryKey: key });
    const src = (photo: PhotoRow, small: boolean) => withSession(`/api${base}/photo/${photo.id}/file${small ? "?small" : ""}`);

    async function addFiles(list: FileList | null) {
        const chosen = [...(list ?? [])];
        if (!chosen.length) return;
        setError(null);
        const room = PHOTOS_MAX - photos.length;
        try {
            if (chosen.length > room) throw new Error(room > 0 ? `There's room for ${plural(room, "more photo")} (${PHOTOS_MAX} at most).` : `It has ${PHOTOS_MAX} photos already: delete one first.`);
            for (const [i, file] of chosen.entries()) {
                setAdding(chosen.length > 1 ? `Adding ${i + 1} of ${chosen.length}...` : "Adding...");
                const photo = await shrink(file, PHOTO_SIDE);
                const small = await shrink(file, SMALL_SIDE);
                const form = new FormData();
                form.set("photo", photo.blob, "photo.jpg");
                form.set("small", small.blob, "small.jpg");
                form.set("width", String(photo.width));
                form.set("height", String(photo.height));
                await api(`${base}/photos/${kind}/${id}`, { body: form });
                await refresh();
            }
        } catch (err) {
            setError(err as Error);
        } finally {
            setAdding(null);
            if (input.current) input.current.value = "";
        }
    }

    const run = (fn: () => Promise<unknown>) => {
        setError(null);
        fn()
            .then(refresh)
            .catch((err: Error) => setError(err));
    };
    const move = (photo: PhotoRow, position: number) => run(() => api(`${base}/photo/${photo.id}`, { method: "PATCH", body: { position } }));
    const caption = (photo: PhotoRow, text: string) => {
        if (text.trim() !== photo.caption) run(() => api(`${base}/photo/${photo.id}`, { method: "PATCH", body: { caption: text } }));
    };
    async function remove(photo: PhotoRow) {
        const yes = await confirm({ title: "Delete this photo?", message: photo.caption || undefined, detail: "It's gone for everyone: it can't be brought back.", confirmLabel: "Delete photo", tone: "danger" });
        if (yes) run(() => api(`${base}/photo/${photo.id}`, { method: "DELETE" }));
    }

    return (
        <Box>
            <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="m-0 text-sm text-muted">
                    {photos.length ? plural(photos.length, "photo") : kind === "group" ? "Photos of what's in this group, to show a customer." : "Photos of this product, to show a customer."}
                </p>
                {editable ? (
                    <>
                        <button type="button" className="btn btn-primary" disabled={adding !== null || photos.length >= PHOTOS_MAX} onClick={() => input.current?.click()}>
                            {adding ? <Spinner className="size-4" /> : <ImagePlus className="size-4" />}
                            {adding ?? "Add photos"}
                        </button>
                        {/* On a phone: the camera, or its photos. */}
                        <input ref={input} type="file" accept="image/*" multiple hidden onChange={(e) => void addFiles(e.target.files)} />
                    </>
                ) : null}
            </div>
            <ErrorNotice error={error ?? query.error} />
            {query.isLoading ? (
                <div className="grid place-items-center p-6">
                    <Spinner />
                </div>
            ) : photos.length ? (
                <ul className="m-0 grid list-none grid-cols-[repeat(auto-fill,minmax(8rem,1fr))] gap-2.5 p-0">
                    {photos.map((photo, i) => (
                        <li key={photo.id} className="relative flex flex-col overflow-hidden rounded-md border border-line bg-surface">
                            {/* Square, whatever its shape: the photo cropped to fill it (in full when opened). */}
                            <button type="button" className="relative block aspect-square overflow-hidden bg-surface-2" title="Show it full size" aria-label="Show it full size" onClick={() => setOpen(photo)}>
                                <PhotoImage src={src(photo, true)} alt={photo.caption || `Photo ${i + 1}`} className="absolute inset-0 size-full object-cover" />
                            </button>
                            {editable ? (
                                <>
                                    {/* Over the photo's corner (always showing: phones can't hover), so the tile can be small. */}
                                    <div className="absolute top-1 right-1 flex rounded-md bg-black/60 text-white">
                                        <TileButton label="Move earlier" disabled={i === 0} onClick={() => move(photo, i - 1)}>
                                            <ChevronLeft className="size-4" />
                                        </TileButton>
                                        <TileButton label="Move later" disabled={i === photos.length - 1} onClick={() => move(photo, i + 1)}>
                                            <ChevronRight className="size-4" />
                                        </TileButton>
                                        <TileButton label="Delete photo" onClick={() => void remove(photo)}>
                                            <Trash2 className="size-4" />
                                        </TileButton>
                                    </div>
                                    <input
                                        // A new one from someone else: shown as it is now.
                                        key={photo.caption}
                                        className="input m-1.5 w-auto min-w-0 px-2 py-1 text-sm"
                                        placeholder="Caption"
                                        aria-label="Caption"
                                        maxLength={PHOTO_CAPTION_MAX}
                                        defaultValue={photo.caption}
                                        onBlur={(e) => caption(photo, e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") e.currentTarget.blur();
                                        }}
                                    />
                                </>
                            ) : photo.caption ? (
                                <p className="m-0 px-2 py-1.5 text-sm">{photo.caption}</p>
                            ) : null}
                        </li>
                    ))}
                </ul>
            ) : (
                <div className="grid place-items-center gap-2 rounded-md border border-dashed border-line p-8 text-sm text-muted">
                    <ImagePlus className="size-6" />
                    No photos yet.
                </div>
            )}
            {open ? (
                <WindowPopup title={open.caption || "Photo"} onClose={() => setOpen(null)} picture>
                    {/* As big as fits in the window, all of it showing. */}
                    <PhotoImage src={src(open, false)} alt={open.caption || "Photo"} className="min-h-0 w-full flex-1 rounded object-contain" />
                    <p className="mt-2 mb-0 shrink-0 text-xs text-muted">
                        Added {new Date(open.at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                        {open.by ? ` by ${open.by}` : ""}
                    </p>
                </WindowPopup>
            ) : null}
        </Box>
    );
}

/** A button on a photo's corner bar (white on dark, over any photo): `label` is its hover text and name. */
function TileButton(props: Readonly<{ label: string; disabled?: boolean; onClick: () => void; children: ReactNode }>) {
    return (
        <button type="button" title={props.label} aria-label={props.label} disabled={props.disabled} onClick={props.onClick} className="rounded-md p-1 hover:bg-white/20 disabled:opacity-35">
            {props.children}
        </button>
    );
}

/** A photo, or (its file isn't here: e.g. a copy of another server's data) saying so. */
function PhotoImage({ src, alt, className }: Readonly<{ src: string; alt: string; className: string }>) {
    const [missing, setMissing] = useState(false);
    if (missing) {
        return (
            <span className="flex size-full min-h-24 flex-col items-center justify-center gap-1 text-xs text-muted">
                <ImageOff className="size-5" />
                Not available here
            </span>
        );
    }
    return <img src={src} alt={alt} className={className} loading="lazy" onError={() => setMissing(true)} />;
}
